'use client';

import {ReactNode, createContext, useCallback, useContext, useMemo, useReducer} from 'react';

export type CartItem = {
    id: number;
    name: string;
    quantity: number;
    price: number;
    imageUrl: string;
    description: string;
    weight: number;
    extra?: {
        id: number;
        name: string;
        price: number;
    };
    additional?: {
        id: number;
        name: string;
        price: number;
        imageUrl: string;
    }[];
};

type CartState = {
    items: CartItem[];
};
const initialState = {items: []};

const checkEqualItemsById = (item: CartItem, item2: CartItem) => {
    if (item.id !== item2.id) return false;
    if (item.extra?.id !== item2.extra?.id) return false;

    if (item.additional && item2.additional) {
        if (item.additional.length !== item2.additional.length) {
            return false;
        }
        for (let i = 0; i < item.additional.length; ++i) {
            if (item.additional[i] !== item2.additional[i]) {
                return false;
            }
        }
        return true;
    }
    return !item.additional && !item2.additional;
};

type Action =
    | {type: 'ADD_ITEM'; payload: CartItem}
    | {type: 'UPDATE_ITEM'; payload: {oldItem: CartItem; newItem: CartItem}}
    | {type: 'REMOVE_ITEM'; payload: CartItem}
    | {type: 'CLEAR_CART'};

const cartReducer = (state: CartState, action: Action) => {
    switch (action.type) {
        case 'ADD_ITEM':
            return {...state, items: [...state.items, action.payload]};
        case 'REMOVE_ITEM':
            return {
                ...state,
                items: state.items.filter((item) => !checkEqualItemsById(item, action.payload)),
            };
        case 'UPDATE_ITEM':
            const newItems = [...state.items];
            const index = newItems.findIndex((item) => checkEqualItemsById(item, action.payload.oldItem));
            if (index === -1) {
                return state;
            }
            newItems[index] = action.payload.newItem;

            return {
                ...state,
                items: newItems,
            };
        case 'CLEAR_CART':
            return {
                ...state,
                items: [],
            };
        default:
            return state;
    }
};

type CartContextType =
    | {
          state: CartState;
          addItem: (item: CartItem) => void;
          removeItem: (item: CartItem) => void;
          updateItem: (oldItem: CartItem, newItem: CartItem) => void;
          clearCart: () => void;
      }
    | undefined;

const CartContext = createContext<CartContextType>(undefined);

export const CartProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const [state, dispatch] = useReducer(cartReducer, initialState);

    const addItem = useCallback(
        (item: CartItem) => {
            const findedItem = state.items.find((item2) => checkEqualItemsById(item, item2));
            if (findedItem) {
                dispatch({
                    type: 'UPDATE_ITEM',
                    payload: {
                        oldItem: findedItem,
                        newItem: {...item, quantity: item.quantity + findedItem.quantity},
                    },
                });
                return;
            }
            dispatch({type: 'ADD_ITEM', payload: item});
        },
        [state],
    );
    const removeItem = useCallback((item: CartItem) => {
        dispatch({type: 'REMOVE_ITEM', payload: item});
    }, []);
    const updateItem = useCallback((oldItem: CartItem, newItem: CartItem) => {
        dispatch({type: 'UPDATE_ITEM', payload: {oldItem, newItem}});
    }, []);

    const clearCart = useCallback(() => {
        dispatch({type: 'CLEAR_CART'});
    }, []);

    return (
        <CartContext.Provider value={{state, addItem, removeItem, updateItem, clearCart}}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

// Хук для получения конкретного элемента корзины + действий с ним
export const useCartItem = (item?: CartItem) => {
    const {addItem, removeItem, updateItem} = useCart();

    // const item = useMemo(() => state.items.find(item => item.id === id), [state.items, id]);

    return {
        item,
        addItem,
        removeItem: () => item && removeItem(item),
        updateItem: (oldItem: CartItem, updatedItem: CartItem) => updateItem(oldItem, updatedItem),
        // Специфичные методы для этого товара
        incrementQuantity: () => {
            if (item) {
                updateItem(item, {...item, quantity: item.quantity + 1});
            }
        },
        decrementQuantity: () => {
            if (!item) {
                return;
            }
            if (item.quantity > 1) {
                updateItem(item, {...item, quantity: item.quantity - 1});
            }
            if (item?.quantity === 1) {
                removeItem(item);
            }
        },
    };
};

// Хук для получения всех элементов корзины + действий
export const useCartItems = () => {
    const {state, addItem, removeItem, updateItem} = useCart();

    const items = useMemo(() => state.items, [state.items]);
    const count = useMemo(() => state.items.length, [state.items]);

    return {
        items,
        count,
        addItem,
        removeItem,
        updateItem,
        // Дополнительные полезные методы
        clearCart: () => {
            items.forEach((item) => removeItem(item));
        },
        findItemAllById: (id: number) => items.filter((item) => item.id === id),
    };
};

// Хук для работы с конкретными свойствами корзины
export const useCartSummary = () => {
    const {state, addItem, removeItem, updateItem} = useCart();

    const summary = useMemo(
        () => ({
            count: state.items.length,
            total: state.items.reduce(
                (sum, item) =>
                    sum +
                    (item.price +
                        (item.extra?.price ?? 0) +
                        (item.additional?.reduce((prev, {price}) => prev + price, 0) ?? 0)) *
                        item.quantity,
                0,
            ),
            uniqueItems: state.items.length,
            totalItems: state.items.reduce((sum, item) => sum + item.quantity, 0),
        }),
        [state.items],
    );

    return {
        ...summary,
        addItem,
        removeItem,
        updateItem,
    };
};

export const getItemTotal = (item: CartItem) => {
    return item.price + (item.extra?.price ?? 0) + (item.additional?.reduce((prev, {price}) => prev + price, 0) ?? 0);
};

export type CartSummaryProps = {
    price: number;
    quantity: number;
}[];

export const getItemsQuantity = (items: CartSummaryProps) => {
    return items.reduce((prev, {quantity}) => prev + quantity, 0);
};

export const generateCartItemId = (item: CartItem): string => {
    const baseId = item.id.toString();

    // Добавляем extra.id, если он есть
    const extraPart = item.extra ? `-ex${item.extra.id}` : '';

    // Добавляем все additionals.id, отсортированные для стабильности
    const additionalsPart = item.additional
        ? `-ad${item.additional
              .map((a) => a.id)
              .sort((a, b) => a - b)
              .join('-')}`
        : '';

    return `${baseId}${extraPart}${additionalsPart}`;
};
